(function () {
  'use strict';
  if (window.__lynkFilmV27Loaded) return;
  window.__lynkFilmV27Loaded = true;
  const instances = new WeakMap();
  const soundKey = 'lynk-christmas-film-sound-v27';
  const pauseKey = 'lynk-christmas-film-paused-v27';
  const read = key => { try { return localStorage.getItem(key); } catch (_) { return null; } };
  const save = (key, value) => { try { localStorage.setItem(key, value); } catch (_) {} };

  function initialize(root, replace) {
    if (instances.has(root)) {
      if (!replace) return;
      instances.get(root).destroy();
    }
    const video = root.querySelector('[data-campaign-video]');
    const controls = root.querySelector('[data-film-controls]');
    if (!video || !controls || video.hidden || !video.getAttribute('src')) return;
    const controller = new AbortController();
    const on = (target, event, callback) => target.addEventListener(event, callback, { signal: controller.signal });
    const sound = controls.querySelector('[data-film-sound]');
    const pause = controls.querySelector('[data-film-pause]');
    const soundLabel = controls.querySelector('[data-film-sound-label]');
    const pauseLabel = controls.querySelector('[data-film-pause-label]');
    const status = controls.querySelector('[data-film-audio-status]');
    const restarts = root.querySelectorAll('[data-film-restart]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const labels = controls.dataset;
    let preference = read(soundKey);
    if (!['on', 'off'].includes(preference)) preference = null;
    let desiredPaused = read(pauseKey) === 'true';
    let operation = 0;
    let nativeGestureUntil = 0;
    let destroyed = false;
    let programmaticVolumeEvents = 0;
    let programmaticPauseEvents = 0;
    const audible = () => !video.muted && video.volume > 0;
    const setMuted = value => { if (video.muted !== value) { ++programmaticVolumeEvents; video.muted = value; } };
    const restoreVolume = () => { if (video.volume === 0) { ++programmaticVolumeEvents; video.volume = 1; } };
    const pauseInternally = () => { if (!video.paused) { ++programmaticPauseEvents; video.pause(); } };
    const nativeGesture = () => Date.now() < nativeGestureUntil;
    const announce = key => { status.textContent = labels[key] || ''; };
    const update = () => {
      const hasSound = audible();
      sound.setAttribute('aria-pressed', String(hasSound));
      soundLabel.textContent = hasSound && (!video.paused || preference === 'on') ? labels.soundOn : preference === 'off' ? labels.soundOff : labels.start;
      sound.setAttribute('aria-label', hasSound && (!video.paused || preference === 'on') ? labels.muteLabel : labels.unmuteLabel);
      sound.querySelector('[data-film-sound-waves]').setAttribute('d', hasSound ? 'M16 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14' : 'm16 8 6 8m0-8-6 8');
      const playing = !video.paused && !video.ended;
      pauseLabel.textContent = playing ? labels.pause : labels.play;
      pause.setAttribute('aria-label', playing ? labels.pauseLabel : labels.playLabel);
      pause.querySelector('[data-film-play-path]').setAttribute('d', playing ? 'M8 5v14M16 5v14' : 'm8 5 11 7-11 7V5');
    };
    const persistPause = value => { desiredPaused = value; save(pauseKey, String(value)); };
    const valid = token => !destroyed && token === operation;
    const play = (token, allowMutedFallback) => {
      const failed = error => {
        if (!valid(token)) return;
        if (allowMutedFallback && error?.name === 'NotAllowedError') {
          setMuted(true);
          update();
          announce('silentStatus');
          play(token, false);
          return;
        }
        controls.dataset.playBlocked = 'true';
        announce('blockedStatus');
        update();
      };
      let pending;
      try { pending = video.play(); } catch (error) { failed(error); return; }
      Promise.resolve(pending).then(() => {
        if (!valid(token)) return;
        delete controls.dataset.playBlocked;
        announce(audible() ? 'playingStatus' : 'silentStatus');
        update();
      }, failed);
    };

    // These buttons act directly in the trusted click; no awaited readiness step.
    on(sound, 'click', () => {
      const token = ++operation;
      if (audible() && (!video.paused || preference === 'on')) {
        preference = 'off'; save(soundKey, preference); setMuted(true);
        announce('mutedStatus'); update(); return;
      }
      preference = 'on'; save(soundKey, preference); persistPause(false);
      setMuted(false);
      restoreVolume();
      if (video.ended) video.currentTime = 0;
      play(token, false);
      update();
    });
    on(pause, 'click', () => {
      const token = ++operation;
      if (!video.paused && !video.ended) {
        persistPause(true); pauseInternally(); announce('pausedStatus'); update(); return;
      }
      persistPause(false);
      if (video.ended) video.currentTime = 0;
      play(token, false);
    });
    restarts.forEach(button => {
      button.hidden = false;
      on(button, 'click', () => {
        const token = ++operation;
        persistPause(false);
        // Keep the native player's current mute AND volume when restarting.
        // A delayed native-control choice is more current than stored intent.
        video.currentTime = 0;
        play(token, false);
        update();
      });
    });
    // Only the native player's own pointer/keyboard actions save native choices.
    on(video, 'pointerdown', () => { nativeGestureUntil = Date.now() + 1000; });
    on(video, 'keydown', () => { nativeGestureUntil = Date.now() + 1000; });
    on(video, 'volumechange', () => {
      if (programmaticVolumeEvents) { --programmaticVolumeEvents; update(); return; }
      ++operation;
      preference = audible() ? 'on' : 'off'; save(soundKey, preference);
      update();
    });
    on(video, 'play', () => {
      if (nativeGesture()) { ++operation; persistPause(false); }
      announce(audible() ? 'playingStatus' : 'silentStatus');
      update();
    });
    on(video, 'playing', () => {
      // Native playback can resume after a visibility pause without a new play promise.
      delete controls.dataset.playBlocked;
      announce(audible() ? 'playingStatus' : 'silentStatus');
      update();
    });
    on(video, 'pause', () => {
      if (programmaticPauseEvents) { --programmaticPauseEvents; update(); return; }
      if (nativeGesture() && !video.ended && !document.hidden) { ++operation; persistPause(true); }
      if (!video.ended) announce('pausedStatus');
      update();
    });
    on(video, 'ended', () => { announce('endedStatus'); update(); });
    on(video, 'error', () => {
      ++operation;
      controls.hidden = true;
      restarts.forEach(button => { button.hidden = true; });
    });
    on(document, 'visibilitychange', () => {
      if (document.hidden && !video.paused) { ++operation; pauseInternally(); announce('pausedStatus'); update(); }
    });
    on(reduced, 'change', () => {
      if (reduced.matches) { ++operation; pauseInternally(); announce('reducedStatus'); update(); }
    });
    const instance = { destroy() {
      destroyed = true; ++operation; controller.abort(); video.pause(); controls.hidden = true;
      restarts.forEach(button => { button.hidden = true; }); instances.delete(root);
    } };
    instances.set(root, instance);
    root.dataset.filmV27 = 'ready';
    controls.hidden = false;
    video.preload = 'metadata';
    setMuted(preference === 'off' || reduced.matches || desiredPaused);
    update();
    if (reduced.matches) { announce('reducedStatus'); return; }
    if (desiredPaused) { announce('pausedStatus'); return; }
    // One audible attempt where permitted, or one muted start for a saved mute.
    // An audible policy rejection gets one muted fallback on this same element.
    play(++operation, preference !== 'off');
  }
  const initializeIn = (container, replace) => {
    if (container.matches?.('[data-lynk-christmas]')) initialize(container, replace);
    container.querySelectorAll?.('[data-lynk-christmas]').forEach(root => initialize(root, replace));
  };
  document.addEventListener('lynk:ready', event => initializeIn(event.target, true));
  document.addEventListener('shopify:section:unload', event => {
    if (instances.has(event.target)) instances.get(event.target).destroy();
    event.target.querySelectorAll?.('[data-lynk-christmas]').forEach(root => instances.get(root)?.destroy());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeIn(document, false), { once: true });
  else initializeIn(document, false);
}());
